import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { API_BASE_URL, fetchJson } from './api';

function localDateTimeValue(value) {
    const date = value ? new Date(value) : new Date(Date.now() + 24 * 60 * 60 * 1000);
    if (!Number.isFinite(date.getTime())) return '';
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
}

function formatAppointmentDate(value) {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return '';
    return date.toLocaleString('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
}

function reportLabel(instance) {
    const match = String(instance || '').match(/^interim:(\d+)$/);
    return match ? `Interim Report ${match[1]}` : 'Initial Report';
}

function projectCycleLabel(project) {
    if (String(project?.in_progress_report_type || '').trim().toLowerCase() !== 'interim') return '';
    const interimNumber = Math.max(1, Math.trunc(Number(project?.in_progress_interim_number) || 1));
    return ` · Interim ${interimNumber}`;
}

export default function SiteVisitDialog({
    open,
    onClose,
    onSaved,
    projects = [],
    initialProjectReference = '',
    initialAppointment = null,
    isAdmin = false,
    source = 'calendar'
}) {
    const [projectReference, setProjectReference] = useState('');
    const [current, setCurrent] = useState(null);
    const [appointment, setAppointment] = useState(null);
    const [mode, setMode] = useState('book');
    const [startsAt, setStartsAt] = useState('');
    const [duration, setDuration] = useState(30);
    const [reason, setReason] = useState('');
    const [surveyorKey, setSurveyorKey] = useState('');
    const [users, setUsers] = useState([]);
    const [busyEvents, setBusyEvents] = useState([]);
    const [calendarWarning, setCalendarWarning] = useState('');
    const [conflicts, setConflicts] = useState([]);
    const [confirmConflict, setConfirmConflict] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        const reference = String(initialProjectReference || initialAppointment?.project_reference || '').trim();
        setProjectReference(reference);
        setAppointment(initialAppointment || null);
        setCurrent(null);
        setMode(initialAppointment ? 'summary' : 'book');
        setStartsAt(localDateTimeValue(initialAppointment?.starts_at));
        const initialDuration = initialAppointment
            ? Math.max(5, Math.round((new Date(initialAppointment.ends_at) - new Date(initialAppointment.starts_at)) / 60000))
            : 30;
        setDuration(Number.isFinite(initialDuration) ? initialDuration : 30);
        setReason('');
        setSurveyorKey(initialAppointment?.surveyor_key || '');
        setBusyEvents([]);
        setCalendarWarning('');
        setConflicts([]);
        setConfirmConflict(false);
        setError('');
    }, [initialAppointment, initialProjectReference, open]);

    useEffect(() => {
        if (!open || !isAdmin) return;
        let cancelled = false;
        fetchJson(`${API_BASE_URL}/api/dashboard/priority-actions`)
            .then((data) => {
                if (!cancelled) setUsers(Array.isArray(data?.users) ? data.users : []);
            })
            .catch(() => {
                if (!cancelled) setUsers([]);
            });
        return () => { cancelled = true; };
    }, [isAdmin, open]);

    useEffect(() => {
        if (!open || !projectReference) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        fetchJson(`${API_BASE_URL}/api/site-visits/current?project_reference=${encodeURIComponent(projectReference)}`)
            .then((data) => {
                if (cancelled) return;
                setCurrent(data);
                if (data?.appointment) {
                    setAppointment(data.appointment);
                    setMode('summary');
                    setStartsAt(localDateTimeValue(data.appointment.starts_at));
                    setSurveyorKey(data.appointment.surveyor_key || '');
                } else {
                    setAppointment(null);
                    setMode('book');
                }
            })
            .catch((requestError) => {
                if (!cancelled) setError(requestError?.message || 'Could not load Site Visit details.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [open, projectReference]);

    useEffect(() => {
        if (!open || !projectReference || !startsAt || mode === 'summary') return;
        const selected = new Date(startsAt);
        if (!Number.isFinite(selected.getTime())) return;
        const rangeStart = new Date(selected);
        rangeStart.setHours(0, 0, 0, 0);
        let cancelled = false;
        const query = new URLSearchParams({
            project_reference: projectReference,
            start: rangeStart.toISOString(),
            days: '1'
        });
        if (surveyorKey) query.set('surveyor_key', surveyorKey);
        fetchJson(`${API_BASE_URL}/api/site-visits/availability?${query.toString()}`)
            .then((data) => {
                if (cancelled) return;
                setBusyEvents((Array.isArray(data?.busy_events) ? data.busy_events : []).filter((event) => (
                    !appointment?.provider_event_id || String(event?.provider_event_id || '') !== String(appointment.provider_event_id)
                )));
                setCalendarWarning(data?.calendar_warning || (!data?.calendar_connected ? 'External calendar not connected. Monsurv bookings are still checked.' : ''));
            })
            .catch((requestError) => {
                if (!cancelled) setCalendarWarning(requestError?.message || 'Availability could not be loaded.');
            });
        return () => { cancelled = true; };
    }, [appointment?.provider_event_id, mode, open, projectReference, startsAt, surveyorKey]);

    const selectedProject = projects.find((project) => String(project?.reference) === projectReference) || null;
    const selectedStartIsPast = startsAt && new Date(startsAt).getTime() < Date.now() - 60000;
    const externalConflictCount = conflicts.filter((conflict) => conflict?.conflict_source === 'external_calendar').length;
    const monsurvConflictCount = conflicts.length - externalConflictCount;
    const conflictSummary = [
        monsurvConflictCount ? `${monsurvConflictCount} Monsurv Site Visit${monsurvConflictCount === 1 ? '' : 's'}` : '',
        externalConflictCount ? `${externalConflictCount} external calendar event${externalConflictCount === 1 ? '' : 's'}` : ''
    ].filter(Boolean).join(' and ');

    async function save() {
        setSaving(true);
        setError('');
        try {
            const start = new Date(startsAt);
            const end = new Date(start.getTime() + Math.max(5, Number(duration) || 30) * 60000);
            const body = {
                starts_at: start.toISOString(),
                ends_at: end.toISOString(),
                confirm_conflict: confirmConflict,
                ...(surveyorKey ? { surveyor_key: surveyorKey } : {})
            };
            let result;
            if (mode === 'reschedule') {
                result = await fetchJson(`${API_BASE_URL}/api/site-visits/${appointment.id}/reschedule`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...body, reason, version: appointment.version })
                });
            } else {
                result = await fetchJson(`${API_BASE_URL}/api/site-visits`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...body,
                        project_reference: projectReference,
                        source,
                        ...(selectedStartIsPast ? { past_date_reason: reason } : {})
                    })
                });
            }
            setAppointment(result?.appointment || null);
            setConflicts([]);
            setMode('summary');
            setCalendarWarning(result?.sync_warning || '');
            onSaved?.(result?.appointment, result);
        } catch (requestError) {
            if (requestError?.body?.requires_confirmation) {
                setConflicts(Array.isArray(requestError.body.conflicts) ? requestError.body.conflicts : []);
                setConfirmConflict(true);
            }
            setError(requestError?.message || 'The Site Visit could not be saved.');
        } finally {
            setSaving(false);
        }
    }

    async function cancelVisit() {
        if (!appointment) return;
        setSaving(true);
        setError('');
        try {
            const result = await fetchJson(`${API_BASE_URL}/api/site-visits/${appointment.id}/cancel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason, version: appointment.version })
            });
            setAppointment(null);
            setCurrent((value) => ({ ...(value || {}), appointment: null, eligible: true, unavailable_reason: '' }));
            setMode('book');
            setReason('');
            setCalendarWarning(result?.sync_warning || '');
            onSaved?.(null, result);
        } catch (requestError) {
            setError(requestError?.message || 'The Site Visit could not be cancelled.');
        } finally {
            setSaving(false);
        }
    }

    const canSave = Boolean(projectReference && startsAt && ((mode !== 'reschedule' && !selectedStartIsPast) || reason.trim()));

    return (
        <Dialog open={Boolean(open)} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{appointment ? 'Manage Site Visit' : 'Book Site Visit'}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 0.5 }}>
                    {error ? <Alert severity={conflicts.length ? 'warning' : 'error'}>{error}</Alert> : null}
                    {calendarWarning ? <Alert severity="info">{calendarWarning}</Alert> : null}
                    {current?.eligible === false && !appointment && current?.unavailable_reason ? (
                        <Alert severity="warning">{current.unavailable_reason}</Alert>
                    ) : null}

                    <FormControl fullWidth disabled={Boolean(initialProjectReference || appointment)}>
                        <InputLabel id="site-visit-project-label">Project</InputLabel>
                        <Select
                            labelId="site-visit-project-label"
                            label="Project"
                            value={projectReference}
                            onChange={(event) => setProjectReference(event.target.value)}
                            MenuProps={{ PaperProps: { sx: { maxHeight: '70vh' } } }}
                        >
                            {projects.map((project) => (
                                <MenuItem key={project.reference} value={project.reference}>
                                    {project.reference} - {project.project_name || project.site_address || 'Project'}{projectCycleLabel(project)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {current?.report_instance ? (
                        <Typography variant="body2" color="text.secondary">
                            {reportLabel(current.report_instance)}{selectedProject?.site_address ? ` · ${selectedProject.site_address}` : ''}
                        </Typography>
                    ) : null}

                    {isAdmin && !appointment ? (
                        <FormControl fullWidth>
                            <InputLabel id="site-visit-surveyor-label">Surveyor</InputLabel>
                            <Select
                                labelId="site-visit-surveyor-label"
                                label="Surveyor"
                                value={surveyorKey}
                                onChange={(event) => {
                                    setSurveyorKey(event.target.value);
                                    setConfirmConflict(false);
                                    setConflicts([]);
                                }}
                            >
                                <MenuItem value="">Project lead surveyor</MenuItem>
                                {users.map((user) => (
                                    <MenuItem key={user.key} value={user.key}>{user.name || user.email || user.key}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : null}

                    {appointment && mode === 'summary' ? (
                        <Stack spacing={0.5}>
                            <Typography variant="subtitle1">{formatAppointmentDate(appointment.starts_at)}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {appointment.surveyor_name} · {reportLabel(appointment.report_instance)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Calendar sync: {String(appointment.sync_status || 'pending').replace('_', ' ')}
                            </Typography>
                        </Stack>
                    ) : (
                        <>
                            <TextField
                                label="Start"
                                type="datetime-local"
                                value={startsAt}
                                onChange={(event) => {
                                    setStartsAt(event.target.value);
                                    setConfirmConflict(false);
                                    setConflicts([]);
                                }}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                            <TextField
                                label="Duration (minutes)"
                                type="number"
                                value={duration}
                                onChange={(event) => setDuration(event.target.value)}
                                inputProps={{ min: 5, max: 480, step: 5 }}
                                fullWidth
                            />
                        </>
                    )}

                    {mode === 'reschedule' || mode === 'cancel' || (mode === 'book' && selectedStartIsPast) ? (
                        <TextField
                            label={mode === 'cancel'
                                ? 'Cancellation reason'
                                : (mode === 'reschedule' ? 'Reason for change' : 'Reason for recording a past visit')}
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            multiline
                            minRows={2}
                            required
                            fullWidth
                        />
                    ) : null}

                    {mode !== 'summary' && busyEvents.length ? (
                        <Stack spacing={0.5}>
                            <Typography variant="subtitle2">Busy on this day</Typography>
                            {busyEvents.slice(0, 6).map((event, index) => (
                                <Typography key={`${event.provider_event_id || event.start}-${index}`} variant="body2" color="text.secondary">
                                    {formatAppointmentDate(event.start)} · {event.title || 'Busy'}
                                </Typography>
                            ))}
                        </Stack>
                    ) : null}

                    {conflicts.length ? (
                        <Alert severity="warning">
                            This overlaps {conflictSummary}. Submit again to confirm the override.
                        </Alert>
                    ) : null}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Close</Button>
                {appointment && mode === 'summary' ? (
                    <>
                        <Button color="error" onClick={() => { setMode('cancel'); setReason(''); }}>Cancel Visit</Button>
                        <Button variant="contained" onClick={() => { setMode('reschedule'); setReason(''); }}>Change Visit</Button>
                    </>
                ) : null}
                {mode === 'cancel' ? (
                    <Button color="error" variant="contained" onClick={cancelVisit} disabled={saving || !reason.trim()}>
                        {saving ? 'Cancelling...' : 'Confirm Cancellation'}
                    </Button>
                ) : null}
                {mode === 'book' || mode === 'reschedule' ? (
                    <Button variant="contained" onClick={save} disabled={saving || loading || !canSave || (current?.eligible === false && !appointment)}>
                        {saving ? 'Saving...' : (confirmConflict ? 'Confirm Override' : (mode === 'reschedule' ? 'Save Change' : 'Book Visit'))}
                    </Button>
                ) : null}
            </DialogActions>
        </Dialog>
    );
}